import { AlertTriangle, Shield } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import { flowService } from "@/lib/services";
import type { FlowAuthorizationStatus } from "@/types";
import { BackgroundAccessConsentModal } from "./BackgroundAccessConsentModal";

/**
 * Lets a user explicitly authorize (and re-authorize) background delegated
 * access for a flow's scheduled runs. Mirrors the SharePoint
 * `AutomatedSyncControl`, but for the `scheduled_flows` delegated token.
 *
 * Detection is intentionally NOT based on which tools the flow uses: agentless
 * flow steps (a plain prompt like "check my email") can call delegated tools at
 * runtime, so any scheduled flow may need this authorization. The control is
 * therefore shown whenever background access is enabled org-wide and the flow
 * has at least one scheduled trigger.
 */
export function ScheduledFlowAuthControl({
  flowId,
  hasScheduledTrigger,
}: Readonly<{ flowId: string; hasScheduledTrigger: boolean }>) {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [status, setStatus] = useState<FlowAuthorizationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await flowService.getAuthorization(flowId);
      setStatus(res.data.status);
    } catch {
      // Non-fatal: leave status unknown and hide the control.
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  // Only fetch status when the control could actually render — avoids a needless
  // API call when background access is off org-wide or the flow has no schedule.
  const cachingEnabled = Boolean(profile?.capabilities?.automatedSyncEnabled);
  const eligible = cachingEnabled && hasScheduledTrigger;

  useEffect(() => {
    if (!eligible) return;
    void load();
  }, [eligible, load]);

  // Backend-gated: hide entirely when delegated token caching is off org-wide,
  // when this flow has no scheduled trigger, or while the initial status loads.
  if (!eligible) return null;
  if (loading || status === null || status === "disabled") return null;

  const authorized = status === "authorized";
  const needsReauth = status === "needs_reauth";

  const authorize = async () => {
    try {
      setBusy(true);
      const res = await flowService.authorize(flowId);
      setStatus(res.data.status);
      setShowConsent(false);
      showToast("Background access authorized", "success");
    } catch (err) {
      showToast(getApiErrorMessage(err) || "Failed to authorize background access", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 px-4 py-3 bg-surface-secondary/50 rounded-lg">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-2 min-w-0">
          <Shield className="h-4 w-4 text-content-tertiary mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-content-primary">Background access</p>
            <p className="text-xs text-content-tertiary">
              {authorized
                ? "This workflow can run on your behalf using your Microsoft permissions, even when you're offline."
                : "Authorize this workflow to run on your behalf (e.g. read your email or SharePoint) when it runs on a schedule."}
            </p>
          </div>
        </div>

        <div className="shrink-0">
          {authorized ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-400">
              Authorized
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setShowConsent(true)}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary/90 rounded-lg disabled:opacity-50"
            >
              {needsReauth ? "Re-authorize" : "Authorize"}
            </button>
          )}
        </div>
      </div>

      {needsReauth && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          Your saved permission is no longer valid. Re-authorize to keep scheduled runs working.
        </p>
      )}

      {showConsent && (
        <BackgroundAccessConsentModal
          busy={busy}
          onCancel={() => setShowConsent(false)}
          onConfirm={() => void authorize()}
        />
      )}
    </div>
  );
}
